import { useMemo } from "react";
import { toStarRating } from "@b2bportal/core-utilities";
import {
  CancellationPolicyEnum,
  CheckInTimeEnum,
  LodgingMediaAssetEnum,
  type Reservation,
} from "@b2bportal/lodging-api";
import { DateTimeFormatStyle, useI18nContext } from "@hopper-b2b/i18n";
import { htmlDecode } from "@hopper-b2b/utilities";
import dayjs from "dayjs";
import { formatGuestSummary } from "../../utils/formatGuests";

const DATE_FORMAT = DateTimeFormatStyle.ShortMonthDayShortWeekday;

export const useTripSummaryCard = (reservation: Reservation) => {
  const { t, formatDateTime } = useI18nContext();

  return useMemo(() => {
    const lodgingData = reservation.lodgingData;
    if (lodgingData == null) return null;

    const image =
      reservation.roomInfo?.media
        ?.filter((m) => m.type !== LodgingMediaAssetEnum.Video)
        ?.at(0)?.url ??
      lodgingData.media
        ?.filter((m) => m.type !== LodgingMediaAssetEnum.Video)
        ?.at(0)?.url;

    const checkInInstructions = lodgingData.checkInInstructions;

    const checkInTimeStr =
      checkInInstructions?.checkInTime?.CheckInTime ===
      CheckInTimeEnum.Restricted
        ? checkInInstructions.checkInTime.checkInTimeFrom
        : undefined;
    const checkOutTimeStr = checkInInstructions?.checkOutTime;

    const formatDateWithTime = (date: string, time?: string) => {
      const formatted = formatDateTime(dayjs(date).toDate(), DATE_FORMAT);
      if (time) {
        const parsed = dayjs(`2000-01-01T${time}`);
        if (parsed.isValid()) {
          const formattedTime = formatDateTime(
            parsed.toDate(),
            DateTimeFormatStyle.ShortTime
          );
          return `${formatted} (${formattedTime})`;
        }
      }
      return formatted;
    };

    const checkInDate = formatDateWithTime(
      reservation.checkInDate,
      checkInTimeStr
    );
    const checkOutDate = formatDateWithTime(
      reservation.checkOutDate,
      checkOutTimeStr
    );

    const starRating = toStarRating(lodgingData.starRating);
    const location = [lodgingData.city, lodgingData.country]
      .filter(Boolean)
      .join(", ");
    const address =
      typeof lodgingData.address === "string"
        ? lodgingData.address
        : [
            lodgingData.address?.line1,
            lodgingData.address?.line2,
            lodgingData.address?.city,
            lodgingData.address?.stateOrProvince,
            lodgingData.address?.postalCode,
          ]
            .filter(Boolean)
            .join(", ");

    const rawRoomName =
      reservation.bookedRooms?.[0]?.roomInfo?.name ??
      reservation.roomInfo?.name ??
      "";
    const roomName = htmlDecode(rawRoomName) ?? rawRoomName;
    const beds =
      reservation.bookedRooms?.[0]?.roomInfo?.beds?.description ??
      reservation.roomInfo?.beds?.description ??
      "";
    const adultCount = reservation.adultCount ?? 0;
    const childCount = reservation.childCount ?? 0;
    const childrenAges = reservation.childrenAges ?? [];
    const guestSummary = formatGuestSummary(
      t,
      adultCount,
      childCount,
      childrenAges
    );

    const tripAdvisorReviews = lodgingData.tripAdvisorReviews;
    const tripAdvisorScore =
      tripAdvisorReviews != null ? tripAdvisorReviews.overallScore * 5 : null;
    const tripAdvisorReviewCount = tripAdvisorReviews?.reviewsCount ?? null;

    const cancellationPolicy = reservation.cancellationPolicy;
    const isRefundable =
      cancellationPolicy?.CancellationPolicy ===
      CancellationPolicyEnum.Refundable;
    const cancellationPrimaryText = cancellationPolicy?.primaryText ?? null;

    return {
      image,
      name: lodgingData.name,
      starRating,
      location,
      address,
      checkInDate,
      checkOutDate,
      roomName,
      beds,
      guestSummary,
      tripAdvisorScore,
      tripAdvisorReviewCount,
      isRefundable,
      cancellationPrimaryText,
    };
  }, [reservation, formatDateTime, t]);
};
